import { useNavigate } from "react-router-dom";
import { ArrowLeft, Scissors, Settings, LogOut, Map, Tv } from "lucide-react";
import { GlossLogomark } from "@/components/GlossLogomark";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useBranch } from "@/contexts/BranchContext";
import { useState } from "react";
import { SettingsModal } from "@/components/SettingsModal";

export function RoleShell({ title, accent = "gold", showFloorMap = false, rightExtras = null, children }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { settings } = useSettings();
  const { branches, activeBranchId, selectBranch } = useBranch();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <header
        className="fixed top-0 left-0 right-0 z-40 glass-primary"
        style={{ height: 68 }}
        data-testid="role-shell-topbar"
      >
        <div className="h-full max-w-[1700px] mx-auto px-6 flex items-center gap-4">
          <button
            onClick={() => navigate("/personas")}
            className="btn-3d btn-glass-3d btn-sm"
            aria-label="Back to personas"
            data-testid="topbar-back"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div className="flex items-center gap-3">
            <GlossLogomark size={36} animate={false} />
            <div className="leading-tight">
              <div className="font-display font-bold text-[16px] text-[var(--color-text-primary)]">GLOSS</div>
              <div className="font-inter text-[11px] text-[var(--color-text-muted)] -mt-0.5">{title}</div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 ml-3 px-3 py-1.5 rounded-full glass-subtle">
            <span className="inline-block w-2 h-2 rounded-full bg-[var(--color-success)] live-dot" />
            <span className="font-mono text-[12px] text-[var(--color-text-secondary)]">Live</span>
          </div>

          <button
            onClick={() => setOpen(true)}
            className="hidden md:flex items-center gap-2 ml-2 px-3 py-1.5 rounded-full glass-subtle hover:brightness-105 transition"
            data-testid="topbar-salon-chip"
          >
            <Scissors size={14} className="text-[var(--color-mauve)]" />
            <span className="font-sora text-[13px] font-semibold text-[var(--color-text-primary)]">{settings.salon_name}</span>
            <span className="font-inter text-[12px] text-[var(--color-text-muted)]">·</span>
            <select
              value={activeBranchId}
              onChange={(e) => selectBranch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="bg-transparent font-inter text-[12px] text-[var(--color-text-secondary)] outline-none cursor-pointer"
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </button>

          <div className="flex-1" />

          {rightExtras}

          {showFloorMap && (
            <button
              onClick={() => navigate("/floormap")}
              className="btn-3d btn-glass-3d btn-sm"
              aria-label="Floor Map"
              data-testid="topbar-floormap"
            >
              <Map size={16} /> Floor
            </button>
          )}

          <button
            onClick={() => window.open("/display", "_blank")}
            className="btn-3d btn-glass-3d btn-sm"
            data-testid="topbar-display"
          >
            <Tv size={16} /> Display
          </button>

          <button
            onClick={() => setOpen(true)}
            className="w-10 h-10 rounded-full glass-subtle flex items-center justify-center hover:brightness-105 transition"
            aria-label="Settings"
            data-testid="topbar-settings"
          >
            <Settings size={16} className="text-[var(--color-text-secondary)]" />
          </button>

          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="w-10 h-10 rounded-full glass-subtle flex items-center justify-center hover:brightness-105 transition"
            aria-label="Logout"
            data-testid="topbar-logout"
          >
            <LogOut size={16} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>
      </header>

      <main style={{ paddingTop: 84, paddingBottom: 60 }}>{children}</main>

      {open && <SettingsModal onClose={() => setOpen(false)} />}
    </div>
  );
}

export default RoleShell;
